// /// MarketAlerts.jsx
import React, { useState, useEffect } from "react";
import { Modal, Button, message, Spin, List, Typography } from "antd";
import AlertTable from "./AlertTable";
import AlertEditor from "./AlertEditor";
import ClauseEditor from "./ClauseEditor";
import UrgencyConfigSection from "./UrgencyConfigSection"; // DAS-2532
import { fetchAlerts, deleteAlert, updateAlert, fetchClauses, createAlert, fetchAlertEffectiveness, fetchLLMRecommendations, addAffirmativeClause, fetchClauseTweaks, fetchClauseProposals, addKnockoutClause, promoteAlertToProd } from "../functions/alertClauseService";
import useFetchDashboardStructure from "../functions/FetchDashboardStructure";

const isProdEnvironment = () => {
  const hostname = window.location.hostname;
  return hostname.includes('monosoft.online') || hostname.includes('37.97.140.217');
};

const MarketAlerts = ({ isVisible, onClose, alertSubtypeIdentifier }) => {
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alertsError, setAlertsError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [clauses, setClauses] = useState([]);
  const [alertEffectiveness, setAlertEffectiveness] = useState([]); // Store alert effectiveness data
  const {dashboardStructure, loading: dashboardLoading, error: dashboardError } = useFetchDashboardStructure(isVisible, 'dashboard');
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [llmVisible, setLlmVisible] = useState(false);
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmRecs, setLlmRecs] = useState([]);
  const [llmAlertInfo, setLlmAlertInfo] = useState(null);
  const [currentAlertId, setCurrentAlertId] = useState(null);
  
  // Clause tweaks state
  const [isClauseTweaksModalVisible, setIsClauseTweaksModalVisible] = useState(false);
  const [clauseTweaksLoading, setClauseTweaksLoading] = useState(false);
  const [clauseTweaksSuggestions, setClauseTweaksSuggestions] = useState([]);
  const [clauseTweaksAlertInfo, setClauseTweaksAlertInfo] = useState(null);
  const [currentClauseTweaksAlertId, setCurrentClauseTweaksAlertId] = useState(null);
  
  // ✨ NEW: Track replaced suggestions and modal state (DAS-1209)
  const [replacedSuggestions, setReplacedSuggestions] = useState(new Set());
  const [isProcessingTweak, setIsProcessingTweak] = useState(false);
  const [processingSuggestionIndex, setProcessingSuggestionIndex] = useState(null);
  
  // Clause editing state
  const [editingClause, setEditingClause] = useState(null);
  const [isEditingClauseFromTweak, setIsEditingClauseFromTweak] = useState(false);
  const [isEditingClauseFromProposal, setIsEditingClauseFromProposal] = useState(false);
  
  // Clause proposals state
  const [isClauseProposalsModalVisible, setIsClauseProposalsModalVisible] = useState(false);
  const [clauseProposalsLoading, setClauseProposalsLoading] = useState(false);
  const [clauseProposalsSuggestions, setClauseProposalsSuggestions] = useState([]);
  const [clauseProposalsAlertInfo, setClauseProposalsAlertInfo] = useState(null);
  const [currentClauseProposalsAlertId, setCurrentClauseProposalsAlertId] = useState(null);
  
  // ✨ NEW: Track processed proposals (similar to replaced suggestions for tweaks)
  const [processedProposals, setProcessedProposals] = useState(new Set());
  const [isProcessingProposal, setIsProcessingProposal] = useState(false);
  const [processingProposalIndex, setProcessingProposalIndex] = useState(null);

  // DAS-2273: Copy-to-PROD state
  const [isCopyToProdModalVisible, setIsCopyToProdModalVisible] = useState(false);
  const [copyToProdLoading, setCopyToProdLoading] = useState(false);
  const [copyToProdPreview, setCopyToProdPreview] = useState(null);
  const [copyToProdAlertId, setCopyToProdAlertId] = useState(null);
  const [copyToProdOverwrite, setCopyToProdOverwrite] = useState(false);

  // console.log("alertSubtypeIdentifier", alertSubtypeIdentifier)

  // console.log("dasboardstructure in marketalerts:", dashboardStructure)

  // Fetch alert effectiveness data
  const loadAlertEffectiveness = async () => {
    try {
      const effectivenessData = await fetchAlertEffectiveness();
      // console.log("Alert effectiveness data in Market Alerts:", effectivenessData);
      setAlertEffectiveness(effectivenessData);
    } catch (error) {
      console.error("Error fetching alert effectiveness data:", error);
      message.error("Failed to load alert effectiveness data.");
    }
  };

  // Fetch all alerts
  const loadAlerts = async () => {
    setAlertsLoading(true);
    try {
      const data = await fetchAlerts();
      // console.log("Alerts data in Market Alerts:", data);
      setAlerts(data);
      setAlertsError(null);
    } catch (error) {
      setAlertsError("Error fetching alerts");
    } finally {
      setAlertsLoading(false);
    }
  };

  useEffect(() => {
    const fetchAlertsData = async () => {
      try {
        await loadAlerts(); // Fetch alerts and set them in state
        loadAlertEffectiveness(); // Fetch effectiveness data
      } catch (error) {
        console.error("Error fetching alerts or effectiveness data:", error);
        setAlertsError("Failed to fetch alerts or effectiveness data.");
      }
    };
  
    if (isVisible) {
      fetchAlertsData(); // Only fetch when the modal is visible
    }
  }, [isVisible]);
  
  useEffect(() => {
    if (alerts.length > 0 && alertSubtypeIdentifier) {
      const identifiers = alertSubtypeIdentifier.split(',').map(id => id.trim());
      // console.log("Identifiers for filtering:", identifiers);
  
      const filtered = alerts.filter(alert => {
        const alertIdentifier = String(alert.ID).trim();
        const matchFound = identifiers.includes(alertIdentifier);
  
    
        return matchFound;
      });
  
      // console.log("Filtered alerts:", filtered);
      setFilteredAlerts(filtered);
    } else if (alerts.length > 0) {
      setFilteredAlerts(alerts); // No identifier, show all alerts
    }
  }, [alerts, alertSubtypeIdentifier]);
  

  useEffect(() => {
    if (isVisible) {
      loadAlerts(); // Fetch alerts when modal is visible
      loadAlertEffectiveness(); // Fetch alert effectiveness data
    }
  }, [isVisible]);

  const handleEditAlert = async (alertId) => {
    const alertToEdit = alerts.find((alert) => alert.ID === alertId);
    setSelectedAlert(alertToEdit);
    setIsEditing(true);
    try {
      const clauseData = await fetchClauses();
      setClauses(clauseData);
    } catch (error) {
      console.error("Error fetching clauses:", error);
    }
  };

  const handleDeleteAlert = (alertId) => {
    Modal.confirm({
      title: "Are you sure you want to delete this alert?",
      onOk: async () => {
        try {
          await deleteAlert(alertId);
          setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.ID !== alertId));
        } catch (error) {
          console.error("Error deleting alert:", error);
        }
      },
    });
  };

  const handleSaveAlert = async (alertData, action) => {
    try {
      let savedAlert;
      if (action === "update") {
        savedAlert = await updateAlert(alertData);
      } else if (action === "create") {
        savedAlert = await createAlert(alertData);
      }
      await loadAlerts(); // Refresh alerts
      const updatedClauses = await fetchClauses(); // Fetch updated clauses
      setClauses(updatedClauses); // Update clauses in state
  
      if (action === "create") {
        setSelectedAlert(savedAlert); // Set the newly created alert as selected
      }
  
      message.success("Alert saved successfully!");
    } catch (error) {
      console.error("Error saving alert:", error);
      message.error("Failed to save alert.");
    }
  };
  
  

  const handleAddAlert = async () => {
    setSelectedAlert(null);
    setIsEditing(true);
    try {
      const clauseData = await fetchClauses();
      setClauses(clauseData);
    } catch (error) {
      console.error("Error fetching clauses:", error);
    }
  };

  const openAffirmationModal = async () => {
    try {
      if (!filteredAlerts || filteredAlerts.length === 0) {
        message.warning("Select or create an alert first.");
        return;
      }
      const alertId = filteredAlerts[0]?.ID || selectedAlert?.ID; // naive: first filtered or selected
      if (!alertId) {
        message.warning("No alert selected.");
        return;
      }
      setLlmVisible(true);
      setLlmLoading(true);
      setCurrentAlertId(alertId);
      const response = await fetchLLMRecommendations(alertId);
      if (response && response.recommendations) {
        setLlmRecs(Array.isArray(response.recommendations) ? response.recommendations : []);
        setLlmAlertInfo(response.alert_info);
      } else {
        // Fallback for old response format
        setLlmRecs(Array.isArray(response) ? response : []);
        setLlmAlertInfo(null);
      }
    } catch (e) {
      setLlmRecs([]);
      setLlmAlertInfo(null);
    } finally {
      setLlmLoading(false);
    }
  };

  const openAffirmationForAlert = async (alertId) => {
    try {
      if (!alertId) {
        message.warning("No alert id provided.");
        return;
      }
      setLlmVisible(true);
      setLlmLoading(true);
      setCurrentAlertId(alertId);
      const response = await fetchLLMRecommendations(alertId);
      if (response && response.recommendations) {
        setLlmRecs(Array.isArray(response.recommendations) ? response.recommendations : []);
        setLlmAlertInfo(response.alert_info);
      } else {
        // Fallback for old response format
        setLlmRecs(Array.isArray(response) ? response : []);
        setLlmAlertInfo(null);
      }
    } catch (e) {
      setLlmRecs([]);
      setLlmAlertInfo(null);
    } finally {
      setLlmLoading(false);
    }
  };

  // Handle accepting a clause recommendation
  const handleAcceptClause = async (clauseId) => {
    try {
      if (!currentAlertId) {
        message.error('No alert selected');
        return;
      }
      
      await addAffirmativeClause(currentAlertId, clauseId);
      
      // Refresh the LLM recommendations to show updated alert info
      const response = await fetchLLMRecommendations(currentAlertId);
      if (response && response.recommendations) {
        setLlmRecs(Array.isArray(response.recommendations) ? response.recommendations : []);
        setLlmAlertInfo(response.alert_info);
      }
      
      message.success(`Clause ${clauseId} added to affirmative clauses!`);
    } catch (error) {
      console.error('Error accepting clause:', error);
    }
  };

  // Handle declining a clause recommendation
  const handleDeclineClause = (clauseId) => {
    // Remove the declined clause from the recommendations list
    setLlmRecs(prevRecs => prevRecs.filter(rec => rec.clause_id !== clauseId));
    message.info(`Clause ${clauseId} declined`);
  };

  // Handle opening clause tweaks for a specific alert
  const openClauseTweaksForAlert = async (alertId) => {
    try {
      if (!alertId) {
        message.warning("No alert id provided.");
        return;
      }
      setIsClauseTweaksModalVisible(true);
      setClauseTweaksLoading(true);
      setCurrentClauseTweaksAlertId(alertId);
      
      // ✨ NEW: Reset replaced suggestions when loading new tweaks (DAS-1209)
      setReplacedSuggestions(new Set());
      
      const response = await fetchClauseTweaks(alertId, 3);
      console.log("Clause tweaks response:", response);
      if (response && response.success) {
        setClauseTweaksSuggestions(response.suggestions || []);
        // Combine alert_info with affirmative_clauses for easier access
        const alertInfo = {
          ...response.alert_info,
          affirmative_clauses: response.affirmative_clauses || []
        };
        console.log("Setting clauseTweaksAlertInfo:", alertInfo);
        setClauseTweaksAlertInfo(alertInfo);
      } else {
        setClauseTweaksSuggestions([]);
        setClauseTweaksAlertInfo(null);
        message.error(response?.error || "Failed to fetch clause tweaks");
      }
    } catch (error) {
      console.error("Error fetching clause tweaks:", error);
      message.error("Failed to fetch clause tweaks.");
      setClauseTweaksSuggestions([]);
      setClauseTweaksAlertInfo(null);
    } finally {
      setClauseTweaksLoading(false);
    }
  };

  // Handle opening clause proposals for a specific alert
  const openClauseProposalsForAlert = async (alertId) => {
    try {
      if (!alertId) {
        message.warning("No alert id provided.");
        return;
      }
      setIsClauseProposalsModalVisible(true);
      setClauseProposalsLoading(true);
      setCurrentClauseProposalsAlertId(alertId);
      
      // ✨ NEW: Reset processed proposals when loading new proposals
      setProcessedProposals(new Set());
      
      const response = await fetchClauseProposals(alertId, 3);
      console.log("Clause proposals response:", response);
      if (response && response.success) {
        setClauseProposalsSuggestions(response.proposals || []);
        setClauseProposalsAlertInfo(response.alert_info);
      } else {
        setClauseProposalsSuggestions([]);
        setClauseProposalsAlertInfo(null);
        message.error(response?.error || "Failed to fetch clause proposals");
      }
    } catch (error) {
      console.error("Error fetching clause proposals:", error);
      message.error("Failed to fetch clause proposals.");
      setClauseProposalsSuggestions([]);
      setClauseProposalsAlertInfo(null);
    } finally {
      setClauseProposalsLoading(false);
    }
  };

  // Handle accepting a clause proposal
  const handleAcceptClauseProposal = (proposalIndex) => {
    const proposal = clauseProposalsSuggestions[proposalIndex];
    
    // Create a pre-filled clause object for the ClauseEditor
    const preFilledClause = {
      clause_field: proposal.clause_field,
      clause_operator: proposal.operator,
      threshold: proposal.threshold,
      clause_field_sourcetable: proposal.source_table,
      clause_txt: proposal.template_text,
      // Set other required fields to defaults
      clause_field_target: null,
      clause_field_target_value: null,
      threshold_sourcetable: null,
      threshold_field: null,
      threshold_field_target: null,
      threshold_field_target_value: null,
      invisible: proposal.role === 'KO' // KO clauses are typically invisible
    };
    
    // Set up for clause editing
    setEditingClause(preFilledClause);
    setIsEditingClauseFromTweak(false); // This is from proposals, not tweaks
    setIsEditingClauseFromProposal(true); // This is from proposals
    setIsProcessingProposal(true); // Track that we're processing this proposal
    setProcessingProposalIndex(proposalIndex); // Track which proposal we're processing
    setIsClauseProposalsModalVisible(false); // Close the proposals modal
  };

  // Handle declining a clause proposal
  const handleDeclineClauseProposal = (proposalIndex) => {
    // Remove the declined proposal from the list
    setClauseProposalsSuggestions(prevSuggestions => 
      prevSuggestions.filter((_, index) => index !== proposalIndex)
    );
    message.info(`Clause proposal ${proposalIndex + 1} declined`);
  };

  // Handle accepting a clause tweak suggestion
  const handleAcceptClauseTweak = (suggestionIndex) => {
    const suggestion = clauseTweaksSuggestions[suggestionIndex];
    
    // Create a pre-filled clause object for the ClauseEditor
    const preFilledClause = {
      clause_field: suggestion.suggested_field,
      clause_operator: suggestion.suggested_operator,
      threshold: suggestion.suggested_threshold,
      clause_field_sourcetable: suggestion.suggested_source_table,
      clause_txt: suggestion.suggested_clause_txt,
      // ✨ NEW: Add original clause ID for Save and Replace functionality (DAS-1209)
      original_clause_id: suggestion.original_clause_id,
      // Add any other fields that might be needed
      clause_field_target: 'ticker', // Default value
      clause_field_target_value: 'ticker', // Default value
      threshold_sourcetable: null,
      threshold_field: null,
      threshold_field_target_value: null,
      threshold_field_target: null,
      invisible: false
    };
    
    // ✨ NEW: Keep modal open but track processing state (DAS-1209)
    setIsProcessingTweak(true);
    setProcessingSuggestionIndex(suggestionIndex);
    
    // Set flag to indicate this is from a tweak
    setIsEditingClauseFromTweak(true);
    
    // Open the ClauseEditor with pre-filled data
    setEditingClause(preFilledClause);
    
    message.success(`Clause tweak suggestion ${suggestionIndex + 1} accepted - opening clause editor`);
  };

  // Handle declining a clause tweak suggestion
  const handleDeclineClauseTweak = (suggestionIndex) => {
    // Remove the declined suggestion from the list
    setClauseTweaksSuggestions(prevSuggestions => 
      prevSuggestions.filter((_, index) => index !== suggestionIndex)
    );
    message.info(`Clause tweak suggestion ${suggestionIndex + 1} declined`);
  };

  // Handle closing clause editor
  const handleCloseClauseEditor = () => {
    // ✨ NEW: If we were processing a tweak, return to the tweaks modal (DAS-1209)
    if (isProcessingTweak) {
      setIsProcessingTweak(false);
      setProcessingSuggestionIndex(null);
      // Modal should already be visible, but ensure it's shown
      setIsClauseTweaksModalVisible(true);
    }
    
    // ✨ NEW: If we were editing from a proposal, return to the proposals modal
    if (isEditingClauseFromProposal) {
      // Modal should already be visible, but ensure it's shown
      setIsClauseProposalsModalVisible(true);
    }
    
    // ✨ NEW: Reset proposal processing state
    if (isProcessingProposal) {
      setIsProcessingProposal(false);
      setProcessingProposalIndex(null);
    }
    
    setEditingClause(null);
    setIsEditingClauseFromTweak(false);
    setIsEditingClauseFromProposal(false);
  };

  // ✨ NEW: Handle when a clause is successfully replaced (DAS-1209)
  const handleClauseReplaced = (originalClauseId, newClauseId) => {
    // Mark this suggestion as replaced
    if (processingSuggestionIndex !== null) {
      setReplacedSuggestions(prev => new Set([...prev, processingSuggestionIndex]));
    }
    
    // Close the clause editor and return to tweaks modal
    setEditingClause(null);
    setIsEditingClauseFromTweak(false);
    setIsProcessingTweak(false);
    setProcessingSuggestionIndex(null);
    
    // Show the tweaks modal with updated state
    setIsClauseTweaksModalVisible(true);
    
    message.success(`Clause ${originalClauseId} replaced with clause ${newClauseId} - returning to suggestions`);
  };

  // ✨ NEW: Handle when a proposal clause is successfully added to alert
  const handleProposalClauseSaved = async (newClauseId, clauseType) => {
    try {
      // Add the clause to the alert in the database
      if (clauseType === 'AFF') {
        // Add as affirmative clause
        await addAffirmativeClause(currentClauseProposalsAlertId, newClauseId);
        message.success(`Clause ${newClauseId} added as affirmative clause to Alert ${currentClauseProposalsAlertId}`);
      } else {
        // Add as knock-out clause
        await addKnockoutClause(currentClauseProposalsAlertId, newClauseId);
        message.success(`Clause ${newClauseId} added as knockout clause to Alert ${currentClauseProposalsAlertId}`);
      }
      
      // Mark this proposal as processed
      if (processingProposalIndex !== null) {
        setProcessedProposals(prev => new Set([...prev, processingProposalIndex]));
      }
      
      // Close the clause editor and return to proposals modal
      setEditingClause(null);
      setIsEditingClauseFromProposal(false);
      setIsProcessingProposal(false);
      setProcessingProposalIndex(null);
      
      // Refresh the proposals to show updated alert info
      const response = await fetchClauseProposals(currentClauseProposalsAlertId, 3);
      if (response && response.success) {
        setClauseProposalsSuggestions(response.proposals || []);
        setClauseProposalsAlertInfo(response.alert_info);
      }
      
      // Show the proposals modal with updated state
      setIsClauseProposalsModalVisible(true);
      
    } catch (error) {
      console.error('Error adding clause to alert:', error);
      message.error('Failed to add clause to alert');
    }
  };

  // DAS-2273: Open the Copy-to-PROD preview modal for a given alert.
  // Always starts as a dry run so nothing is written until the user
  // explicitly confirms in the modal.
  const openCopyToProdModal = async (alertId, { overwrite = false } = {}) => {
    setCopyToProdAlertId(alertId);
    setCopyToProdOverwrite(overwrite);
    setIsCopyToProdModalVisible(true);
    setCopyToProdLoading(true);
    setCopyToProdPreview(null);
    try {
      const preview = await promoteAlertToProd(alertId, {
        commit: false,
        overwriteExisting: overwrite,
      });
      setCopyToProdPreview(preview);
    } catch (error) {
      console.error("Error generating Copy-to-PROD preview:", error);
      message.error("Failed to generate Copy-to-PROD preview.");
      setCopyToProdPreview({
        ok: false,
        status: "error",
        error: error?.message || "Unknown error",
      });
    } finally {
      setCopyToProdLoading(false);
    }
  };

  // DAS-2273: Second click — actually write to PROD after the preview has
  // been shown. Uses whatever overwrite flag the user chose in the modal.
  const confirmCopyToProd = async () => {
    if (!copyToProdAlertId) return;
    setCopyToProdLoading(true);
    try {
      const result = await promoteAlertToProd(copyToProdAlertId, {
        commit: true,
        overwriteExisting: copyToProdOverwrite,
      });
      setCopyToProdPreview(result);
      if (result?.ok) {
        message.success(
          `Alert copied to PROD as #${result.prod_alert_id}` +
          (result.clauses_inserted?.length
            ? ` (${result.clauses_inserted.length} new clauses inserted)`
            : "")
        );
      } else if (result?.status === "collision") {
        message.warning(
          `PROD already has this alert (ID ${result.collision_prod_alert_id}). ` +
          "Tick 'Overwrite existing PROD alert' and retry if you want to replace it."
        );
      } else {
        message.error(`Copy to PROD failed: ${result?.error || "unknown error"}`);
      }
    } catch (error) {
      console.error("Error committing Copy-to-PROD:", error);
      message.error("Copy to PROD failed.");
    } finally {
      setCopyToProdLoading(false);
    }
  };

  const closeCopyToProdModal = () => {
    setIsCopyToProdModalVisible(false);
    setCopyToProdPreview(null);
    setCopyToProdAlertId(null);
    setCopyToProdOverwrite(false);
  };

  return (
    <Modal
      title="Creating Market Alerts"
      visible={isVisible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="ok" type="primary" onClick={onClose}>
          OK
        </Button>,
        <Button key="add" type="primary" onClick={handleAddAlert}>
          Add Alert
        </Button>,
        <Button key="aff" onClick={openAffirmationModal}>
          Affirmation clauses
        </Button>,
      ]}
      width={1500}
      zIndex={2000}
      style={{ zIndex: 2000 }}
    >
      {/* DAS-2532: urgency formula levers — collapsed by default */}
      <UrgencyConfigSection />
      <AlertTable
        alerts={filteredAlerts}
        // alerts={alerts}
        alertsLoading={alertsLoading}
        alertsError={alertsError}
        tableTitle="Market Alerts"
        onEditAlert={handleEditAlert}
        onDeleteAlert={handleDeleteAlert}
        alertEffectiveness={alertEffectiveness} // Pass effectiveness data to AlertTable
        onAffirmationClauses={openAffirmationForAlert}
        onClauseTweaks={openClauseTweaksForAlert}
        onClauseProposals={openClauseProposalsForAlert}
        onCopyToProd={isProdEnvironment() ? undefined : openCopyToProdModal}
      />
      <AlertEditor
        visible={isEditing}
        onClose={() => setIsEditing(false)}
        alertData={selectedAlert}
        onSave={handleSaveAlert}
        clauses={clauses}
        dashboardStructure={dashboardStructure}
        refreshAlerts={loadAlerts} // Pass the loadAlerts function to refresh data
      />
      <Modal
        title="LLM Affirmation Clause Suggestions"
        visible={llmVisible}
        onCancel={() => setLlmVisible(false)}
        footer={[
          <Button key="close" onClick={() => setLlmVisible(false)}>Close</Button>
        ]}
        width={900}
      >
        {llmLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
            <div>Analyzing…</div>
          </div>
        ) : (
          <div>
            {/* Alert Information Section */}
            {llmAlertInfo && (
              <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
                <Typography.Title level={5}>Current Alert: [{llmAlertInfo.alert?.ID || currentAlertId}] {llmAlertInfo.alert?.alert_subtype}</Typography.Title>
                <Typography.Paragraph>
                  <strong>Advice:</strong> {llmAlertInfo.alert?.advice_txt}
                </Typography.Paragraph>
                
                <div style={{ marginTop: 12 }}>
                  <Typography.Text strong>Knock-out Clauses:</Typography.Text>
                  <List
                    size="small"
                    dataSource={llmAlertInfo.required_clauses || []}
                    renderItem={(clause) => (
                      <List.Item style={{ padding: '4px 0' }}>
                        <Typography.Text code>[{clause.id}]</Typography.Text> {clause.clause_txt} 
                        <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                          ({clause.clause_field} {clause.clause_operator} {clause.threshold})
                        </Typography.Text>
                      </List.Item>
                    )}
                  />
                </div>
                
                <div style={{ marginTop: 12 }}>
                  <Typography.Text strong>Current Affirmative Clauses:</Typography.Text>
                  <List
                    size="small"
                    dataSource={llmAlertInfo.affirmative_clauses || []}
                    renderItem={(clause) => (
                      <List.Item style={{ padding: '4px 0' }}>
                        <Typography.Text code>[{clause.id}]</Typography.Text> {clause.clause_txt}
                        <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                          ({clause.clause_field} {clause.clause_operator} {clause.threshold})
                        </Typography.Text>
                      </List.Item>
                    )}
                  />
                </div>
              </div>
            )}
            
            {/* LLM Recommendations Section */}
            <Typography.Title level={5}>LLM Recommendations:</Typography.Title>
            <List
              dataSource={llmRecs}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button 
                      key="accept" 
                      type="primary" 
                      size="small"
                      onClick={() => handleAcceptClause(item.clause_id)}
                    >
                      Accept
                    </Button>,
                    <Button 
                      key="decline" 
                      size="small"
                      onClick={() => handleDeclineClause(item.clause_id)}
                    >
                      Decline
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={<>
                      <Typography.Text strong>Clause {item.clause_id}</Typography.Text>
                      <Typography.Text style={{ marginLeft: 8 }} type="secondary">conf: {item.confidence}</Typography.Text>
                    </>}
                    description={
                      <div style={{ fontSize: 12 }}>
                        <div><strong>Text:</strong> {item.clause_text}</div>
                        <div><strong>Field:</strong> {item.field} <strong>Operator:</strong> {item.operator} <strong>Threshold:</strong> {item.threshold}</div>
                        <div><strong>Rationale:</strong> {item.rationale}</div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>
      
      {/* Clause Tweaks Modal */}
      <Modal
        title="Affirmative Clause Improvement Suggestions"
        visible={isClauseTweaksModalVisible}
        onCancel={() => {
          setIsClauseTweaksModalVisible(false);
          // ✨ NEW: Reset state when canceling modal (DAS-1209)
          setReplacedSuggestions(new Set());
          setIsProcessingTweak(false);
          setProcessingSuggestionIndex(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIsClauseTweaksModalVisible(false);
            // ✨ NEW: Reset state when closing modal (DAS-1209)
            setReplacedSuggestions(new Set());
            setIsProcessingTweak(false);
            setProcessingSuggestionIndex(null);
          }}>Close</Button>
        ]}
        width={1000}
      >
        {clauseTweaksLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
            <div>Generating clause improvement suggestions…</div>
          </div>
        ) : (
          <div>
            {/* Alert Information Section */}
            {clauseTweaksAlertInfo && (
              <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
                <Typography.Title level={5}>Current Alert: [{clauseTweaksAlertInfo.alert_id || clauseTweaksAlertInfo.ID || currentAlertId}] {clauseTweaksAlertInfo.alert_subtype}</Typography.Title>
                <Typography.Paragraph>
                  <strong>Advice:</strong> {clauseTweaksAlertInfo.advice_txt}
                </Typography.Paragraph>
                
                <div style={{ marginTop: 12 }}>
                  <Typography.Text strong>Current Affirmative Clauses:</Typography.Text>
                  {console.log("Rendering affirmative clauses:", clauseTweaksAlertInfo.affirmative_clauses)}
                  <List
                    size="small"
                    dataSource={clauseTweaksAlertInfo.affirmative_clauses || []}
                    renderItem={(clause) => {
                      console.log("Rendering clause:", clause);
                      return (
                        <List.Item style={{ padding: '4px 0' }}>
                          <Typography.Text code>[{clause.id}]</Typography.Text> {clause.clause_txt}
                          <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                            ({clause.clause_field} {clause.clause_operator} {clause.threshold})
                          </Typography.Text>
                        </List.Item>
                      );
                    }}
                  />
                </div>
              </div>
            )}
            
            <Typography.Title level={5}>Affirmative Clause Improvement Suggestions:</Typography.Title>
            <List
              dataSource={clauseTweaksSuggestions.sort((a, b) => (b.confidence || 0) - (a.confidence || 0))}
              renderItem={(item, index) => {
                // Find the original clause details
                const originalClause = clauseTweaksAlertInfo?.affirmative_clauses?.find(clause => {
                  const clauseId = clause.id;
                  const suggestionId = item.original_clause_id;
                  
                  // Try exact match first
                  if (clauseId === suggestionId) return true;
                  
                  // Try string comparison
                  if (String(clauseId) === String(suggestionId)) return true;
                  
                  // Try number comparison
                  if (Number(clauseId) === Number(suggestionId)) return true;
                  
                  return false;
                });
                
                // ✨ NEW: Check if this suggestion has been replaced (DAS-1209)
                const isReplaced = replacedSuggestions.has(index);
                
                return (
                  <List.Item
                    actions={
                      isReplaced ? [
                        <div key="replaced" style={{ 
                          padding: '4px 8px', 
                          backgroundColor: '#f6ffed', 
                          border: '1px solid #b7eb8f', 
                          borderRadius: '4px',
                          color: '#52c41a',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ✅ Accepted & Replaced
                        </div>
                      ] : [
                        <Button 
                          key="accept" 
                          type="primary" 
                          size="small"
                          onClick={() => handleAcceptClauseTweak(index)}
                          disabled={isProcessingTweak && processingSuggestionIndex === index}
                        >
                          {isProcessingTweak && processingSuggestionIndex === index ? 'Processing...' : 'Accept'}
                        </Button>,
                        <Button 
                          key="decline" 
                          size="small"
                          onClick={() => handleDeclineClauseTweak(index)}
                          disabled={isProcessingTweak}
                        >
                          Decline
                        </Button>
                      ]
                    }
                  >
                    <List.Item.Meta
                            title={<>
                              <Typography.Text strong>📋 SUGGESTION {index + 1}</Typography.Text>
                              <Typography.Text style={{ marginLeft: 8 }} type="secondary">Confidence: {(item.confidence * 100)?.toFixed(0) || 'N/A'}%</Typography.Text>
                            </>}
                      description={
                        <div style={{ fontSize: 12 }}>
                          <div><strong>Original Clause ID:</strong> {item.original_clause_id}</div>
                          
                          {originalClause ? (
                            <>
                                    <div style={{ marginTop: 8, padding: 12, backgroundColor: '#f0f0f0', borderRadius: 4, border: '1px solid #d9d9d9' }}>
                                      <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: '#1890ff' }}>📝 CURRENT CLAUSE:</div>
                                      <div style={{ marginBottom: 4 }}><strong>Field:</strong> {originalClause.clause_field || 'N/A'}</div>
                                      <div style={{ marginBottom: 4 }}><strong>Operator:</strong> {originalClause.clause_operator || 'N/A'}</div>
                                      <div style={{ marginBottom: 4 }}><strong>Threshold:</strong> {originalClause.threshold || 'None'}</div>
                                      <div style={{ marginBottom: 4 }}><strong>Source Table:</strong> {originalClause.clause_field_sourcetable || 'N/A'}</div>
                                      <div style={{ marginTop: 8, padding: 6, backgroundColor: '#fff', borderRadius: 3, border: '1px solid #e8e8e8' }}>
                                        <strong>Clause Text:</strong> {originalClause.clause_txt || 'N/A'}
                                      </div>
                                    </div>
                              
                                    <div style={{ marginTop: 8, padding: 12, backgroundColor: '#e6f7ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
                                      <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: '#1890ff' }}>🔄 PROPOSED CHANGES:</div>
                                      <div style={{ marginBottom: 4 }}>
                                        <strong>Field:</strong> {originalClause.clause_field || 'N/A'} 
                                        {originalClause.clause_field !== item.suggested_field ? (
                                          <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}> → {item.suggested_field}</span>
                                        ) : (
                                          <span style={{ color: '#52c41a' }}> (unchanged)</span>
                                        )}
                                      </div>
                                      <div style={{ marginBottom: 4 }}>
                                        <strong>Operator:</strong> {originalClause.clause_operator || 'N/A'} 
                                        {originalClause.clause_operator !== item.suggested_operator ? (
                                          <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}> → {item.suggested_operator}</span>
                                        ) : (
                                          <span style={{ color: '#52c41a' }}> (unchanged)</span>
                                        )}
                                      </div>
                                      <div style={{ marginBottom: 4 }}>
                                        <strong>Threshold:</strong> {originalClause.threshold || 'None'} 
                                        {originalClause.threshold !== item.suggested_threshold ? (
                                          <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}> → {item.suggested_threshold}</span>
                                        ) : (
                                          <span style={{ color: '#52c41a' }}> (unchanged)</span>
                                        )}
                                      </div>
                                      <div style={{ marginBottom: 4 }}>
                                        <strong>Source Table:</strong> {originalClause.clause_field_sourcetable || 'N/A'} 
                                        {originalClause.clause_field_sourcetable !== item.suggested_source_table ? (
                                          <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}> → {item.suggested_source_table}</span>
                                        ) : (
                                          <span style={{ color: '#52c41a' }}> (unchanged)</span>
                                        )}
                                      </div>
                                      <div style={{ marginTop: 8, padding: 6, backgroundColor: '#fff', borderRadius: 3, border: '1px solid #91d5ff' }}>
                                        <strong>New Clause Text:</strong> {item.suggested_clause_txt || 'No text provided by LLM'}
                                      </div>
                                    </div>
                            </>
                          ) : (
                            <div style={{ marginTop: 8, padding: 8, backgroundColor: '#fff2e8', borderRadius: 4 }}>
                              <div><strong>⚠️ Original clause {item.original_clause_id} not found in current affirmative clauses</strong></div>
                              <div style={{ marginTop: 8, padding: 8, backgroundColor: '#e6f7ff', borderRadius: 4 }}>
                                <div><strong>🔄 PROPOSED NEW CLAUSE:</strong></div>
                                <div><strong>Field:</strong> {item.suggested_field || 'N/A'}</div>
                                <div><strong>Operator:</strong> {item.suggested_operator || 'N/A'}</div>
                                <div><strong>Threshold:</strong> {item.suggested_threshold || 'N/A'}</div>
                                <div><strong>Source Table:</strong> {item.suggested_source_table || 'N/A'}</div>
                                <div><strong>Text:</strong> {item.suggested_clause_txt || 'No text provided by LLM'}</div>
                              </div>
                            </div>
                          )}
                          
                                <div style={{ marginTop: 12, padding: 10, backgroundColor: '#f9f9f9', borderRadius: 4, border: '1px solid #d9d9d9' }}>
                                  <div style={{ marginBottom: 6 }}>
                                    <strong style={{ color: '#1890ff' }}>📊 CONFIDENCE:</strong> 
                                    <span style={{ marginLeft: 8, padding: '2px 8px', backgroundColor: '#e6f7ff', borderRadius: 3, fontSize: 12 }}>
                                      {(item.confidence * 100)?.toFixed(0) || 'N/A'}%
                                    </span>
                                  </div>
                                  <div style={{ marginBottom: 6 }}>
                                    <strong style={{ color: '#1890ff' }}>💡 RATIONALE:</strong>
                                    <div style={{ marginTop: 4, padding: 6, backgroundColor: '#fff', borderRadius: 3, fontSize: 11, lineHeight: '1.4' }}>
                                      {item.rationale}
                                    </div>
                                  </div>
                                  <div>
                                    <strong style={{ color: '#1890ff' }}>🎯 IMPACT:</strong>
                                    <div style={{ marginTop: 4, padding: 6, backgroundColor: '#fff', borderRadius: 3, fontSize: 11, lineHeight: '1.4' }}>
                                      {item.impact_description}
                                    </div>
                                  </div>
                                </div>
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </div>
        )}
      </Modal>
      
      {/* Clause Proposals Modal */}
      <Modal
        title="New Clause Proposals"
        visible={isClauseProposalsModalVisible}
        onCancel={() => {
          setIsClauseProposalsModalVisible(false);
          // ✨ NEW: Reset processed proposals when closing modal
          setProcessedProposals(new Set());
          setIsProcessingProposal(false);
          setProcessingProposalIndex(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIsClauseProposalsModalVisible(false);
            // ✨ NEW: Reset processed proposals when closing modal
            setProcessedProposals(new Set());
            setIsProcessingProposal(false);
            setProcessingProposalIndex(null);
          }}>Close</Button>
        ]}
        width={1000}
      >
        {clauseProposalsLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
            <div>Generating new clause proposals…</div>
          </div>
        ) : (
          <div>
            {/* Alert Information Section */}
            {clauseProposalsAlertInfo && (
              <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
                <Typography.Title level={5}>Current Alert: {clauseProposalsAlertInfo.alert?.alert_subtype}</Typography.Title>
                <Typography.Paragraph>
                  <strong>Advice:</strong> {clauseProposalsAlertInfo.alert?.advice_txt}
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <strong>Scope:</strong> {clauseProposalsAlertInfo.alert?.scope}
                </Typography.Paragraph>
                
                <div style={{ marginTop: 12 }}>
                  <Typography.Text strong>Current Knock-out Clauses:</Typography.Text>
                  <List
                    size="small"
                    dataSource={clauseProposalsAlertInfo.required_clauses || []}
                    renderItem={(clause) => (
                      <List.Item style={{ padding: '4px 0' }}>
                        <Typography.Text code>[{clause.id}]</Typography.Text> {clause.clause_txt}
                        <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                          ({clause.clause_field} {clause.clause_operator} {clause.threshold})
                        </Typography.Text>
                      </List.Item>
                    )}
                  />
                </div>
                
                <div style={{ marginTop: 12 }}>
                  <Typography.Text strong>Current Affirmative Clauses:</Typography.Text>
                  <List
                    size="small"
                    dataSource={clauseProposalsAlertInfo.affirmative_clauses || []}
                    renderItem={(clause) => (
                      <List.Item style={{ padding: '4px 0' }}>
                        <Typography.Text code>[{clause.id}]</Typography.Text> {clause.clause_txt}
                        <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                          ({clause.clause_field} {clause.clause_operator} {clause.threshold})
                        </Typography.Text>
                      </List.Item>
                    )}
                  />
                </div>
              </div>
            )}
            
            <Typography.Title level={5}>New Clause Proposals (sorted by confidence):</Typography.Title>
            <List
              dataSource={clauseProposalsSuggestions.sort((a, b) => (b.confidence || 0) - (a.confidence || 0))}
              renderItem={(item, index) => {
                // ✨ NEW: Check if this proposal has been processed
                const isProcessed = processedProposals.has(index);
                const isCurrentlyProcessing = isProcessingProposal && processingProposalIndex === index;
                
                return (
                  <List.Item
                    actions={
                      isProcessed ? [
                        <div key="processed" style={{ 
                          padding: '4px 8px', 
                          backgroundColor: '#f6ffed', 
                          border: '1px solid #b7eb8f', 
                          borderRadius: '4px',
                          color: '#52c41a',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ✅ Accepted & Saved
                        </div>
                      ] : isCurrentlyProcessing ? [
                        <div key="processing" style={{ 
                          padding: '4px 8px', 
                          backgroundColor: '#e6f7ff', 
                          border: '1px solid #91d5ff', 
                          borderRadius: '4px',
                          color: '#1890ff',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          Processing...
                        </div>
                      ] : [
                        <Button 
                          key="accept" 
                          type="primary" 
                          size="small"
                          onClick={() => handleAcceptClauseProposal(index)}
                        >
                          Accept
                        </Button>,
                        <Button 
                          key="decline" 
                          size="small"
                          onClick={() => handleDeclineClauseProposal(index)}
                        >
                          Decline
                        </Button>
                      ]
                    }
                  >
                  <List.Item.Meta
                    title={<>
                      <Typography.Text strong>{item.role} Clause (Confidence: {item.confidence})</Typography.Text>
                    </>}
                    description={
                      <div style={{ fontSize: 12 }}>
                        <div><strong>Field:</strong> {item.clause_field} ({item.source_table})</div>
                        <div><strong>Operator:</strong> {item.operator}</div>
                        <div><strong>Threshold:</strong> {item.threshold}</div>
                        <div><strong>Template Text:</strong> {item.template_text}</div>
                        <div><strong>Rationale:</strong> {item.rationale}</div>
                      </div>
                    }
                  />
                </List.Item>
                );
              }}
            />
          </div>
        )}
      </Modal>
      
      {/* Clause Editor for tweak suggestions */}
      {editingClause && (
        <ClauseEditor
          visible={!!editingClause}
          onClose={handleCloseClauseEditor}
          clause={editingClause}
          dashboardStructure={dashboardStructure}
          refreshClauses={() => {}} // No refresh needed for new clauses
          isFromTweak={isEditingClauseFromTweak}
          isFromProposal={isEditingClauseFromProposal}
          alertId={isEditingClauseFromTweak ? currentClauseTweaksAlertId : currentClauseProposalsAlertId}
          onClauseReplaced={handleClauseReplaced}
          onProposalClauseSaved={handleProposalClauseSaved}
        />
      )}

      {/* DAS-2273: Copy Alert Definition from DEV to PROD */}
      <Modal
        title={`Copy Alert ${copyToProdAlertId ?? ''} from DEV to PROD`}
        visible={isCopyToProdModalVisible}
        onCancel={closeCopyToProdModal}
        footer={(() => {
          const preview = copyToProdPreview;
          const isSuccess = preview?.ok && preview?.status === 'success';
          const isCollision = preview?.status === 'collision';
          const isPreview = preview?.ok && preview?.status === 'preview';
          return [
            <Button key="close" onClick={closeCopyToProdModal}>
              {isSuccess ? 'Close' : 'Cancel'}
            </Button>,
            isCollision && !copyToProdOverwrite && (
              <Button
                key="retry-overwrite"
                danger
                onClick={() => openCopyToProdModal(copyToProdAlertId, { overwrite: true })}
                disabled={copyToProdLoading}
              >
                Retry with Overwrite
              </Button>
            ),
            isPreview && (
              <Button
                key="confirm"
                type="primary"
                onClick={confirmCopyToProd}
                disabled={copyToProdLoading}
              >
                {copyToProdOverwrite ? 'Overwrite on PROD' : 'Confirm & Write to PROD'}
              </Button>
            ),
          ].filter(Boolean);
        })()}
        width={900}
        zIndex={2100}
      >
        {copyToProdLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
            <div>Generating Copy-to-PROD preview…</div>
          </div>
        ) : copyToProdPreview ? (
          <div>
            {/* Error state */}
            {!copyToProdPreview.ok && copyToProdPreview.status === 'error' && (
              <div style={{ padding: 12, background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6 }}>
                <Typography.Text type="danger" strong>Error: </Typography.Text>
                <Typography.Text>{copyToProdPreview.error || 'Unknown error'}</Typography.Text>
              </div>
            )}

            {/* Collision state */}
            {copyToProdPreview.status === 'collision' && (
              <div style={{ padding: 12, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, marginBottom: 16 }}>
                <Typography.Text strong>
                  PROD already has an alert def with the same (scope, alert_type, alert_subtype).
                </Typography.Text>
                <br />
                <Typography.Text>
                  Existing PROD alert ID: <Typography.Text code>{copyToProdPreview.collision_prod_alert_id}</Typography.Text>
                </Typography.Text>
                <br />
                <Typography.Text type="secondary">
                  Click "Retry with Overwrite" below to replace it in place, or Cancel to abort.
                </Typography.Text>
              </div>
            )}

            {/* Success or preview state */}
            {(copyToProdPreview.status === 'preview' || copyToProdPreview.status === 'success') && (
              <>
                <div style={{ padding: 12, background: copyToProdPreview.status === 'success' ? '#f6ffed' : '#e6f7ff', border: '1px solid ' + (copyToProdPreview.status === 'success' ? '#b7eb8f' : '#91d5ff'), borderRadius: 6, marginBottom: 16 }}>
                  <Typography.Text strong>
                    {copyToProdPreview.status === 'success'
                      ? `✅ Copied to PROD as alert #${copyToProdPreview.prod_alert_id}`
                      : 'Preview — nothing has been written to PROD yet.'}
                  </Typography.Text>
                  {copyToProdOverwrite && (
                    <div>
                      <Typography.Text type="warning">Overwrite mode is ON — PROD alert will be UPDATED in place.</Typography.Text>
                    </div>
                  )}
                </div>

                <Typography.Title level={5}>Clauses to reuse (already on PROD):</Typography.Title>
                {copyToProdPreview.clauses_reused && copyToProdPreview.clauses_reused.length > 0 ? (
                  <Typography.Paragraph>
                    PROD clause IDs: <Typography.Text code>{copyToProdPreview.clauses_reused.join(', ')}</Typography.Text>
                  </Typography.Paragraph>
                ) : (
                  <Typography.Paragraph type="secondary">None — no fingerprint matches on PROD.</Typography.Paragraph>
                )}

                <Typography.Title level={5}>
                  Clauses to insert on PROD:
                  {copyToProdPreview.status === 'success' && copyToProdPreview.clauses_inserted?.length > 0 && (
                    <Typography.Text style={{ marginLeft: 8 }} type="secondary">
                      (new PROD IDs: {copyToProdPreview.clauses_inserted.join(', ')})
                    </Typography.Text>
                  )}
                </Typography.Title>
                {copyToProdPreview.clauses_to_insert_preview && copyToProdPreview.clauses_to_insert_preview.length > 0 ? (
                  <List
                    size="small"
                    bordered
                    dataSource={copyToProdPreview.clauses_to_insert_preview}
                    renderItem={(clause) => (
                      <List.Item>
                        <div style={{ fontSize: 12 }}>
                          <div>
                            <Typography.Text code>DEV #{clause.ID}</Typography.Text>{' '}
                            <Typography.Text strong>{clause.clause_field_sourcetable}.{clause.clause_field}</Typography.Text>{' '}
                            <Typography.Text>{clause.clause_operator}</Typography.Text>{' '}
                            <Typography.Text>{clause.threshold || (clause.threshold_sourcetable + '.' + clause.threshold_field)}</Typography.Text>
                          </div>
                          <div style={{ color: '#666', marginTop: 2 }}>{clause.clause_txt}</div>
                        </div>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Typography.Paragraph type="secondary">None — every clause already exists on PROD.</Typography.Paragraph>
                )}

                <Typography.Title level={5} style={{ marginTop: 16 }}>Final alert def on PROD:</Typography.Title>
                <Typography.Paragraph style={{ fontSize: 12 }}>
                  <div><strong>clauses CSV (remapped):</strong> <Typography.Text code>{copyToProdPreview.remapped_clauses || '(empty)'}</Typography.Text></div>
                  <div><strong>affirmative_clauses CSV (remapped):</strong> <Typography.Text code>{copyToProdPreview.remapped_affirmative_clauses || '(empty)'}</Typography.Text></div>
                </Typography.Paragraph>

                {copyToProdPreview.warnings && copyToProdPreview.warnings.length > 0 && (
                  <div style={{ padding: 12, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, marginTop: 12 }}>
                    <Typography.Text strong>Warnings:</Typography.Text>
                    <List
                      size="small"
                      dataSource={copyToProdPreview.warnings}
                      renderItem={(w) => <List.Item>{w}</List.Item>}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <Typography.Paragraph type="secondary">No preview available.</Typography.Paragraph>
        )}
      </Modal>
    </Modal>
  );
};

export default MarketAlerts;
